import { watch } from 'vue';
import { useFlag } from '@loadsmart/vue-feature-flags';

import config from '@/config/config';
import routeHelpers from '@satellite/helpers/routes';
import {
  buildLoginQueryWithMcpTransaction,
  completeMcpPortalLogin,
  readMcpTransaction,
  withMcpTransactionQuery
} from '@satellite/helpers/mcp-login-bridge';
import { isCarrierUser } from '@nova/core';
import { createRouter, createWebHistory } from 'vue-router';
import { tryCompleteOrHopMcpLogin } from '@/modules/auth/login-route.helpers.js';
import { onError } from '@/helpers/router-on-error.js';

// A flag not settled within this window fails closed (route treated as disabled), the same
// fallback the Unleash proxy uses when the flag service itself is unreachable.
const FEATURE_FLAG_TIMEOUT_MS = 3000;

// useFlag() needs an active component/app context to resolve (it calls Vue's inject()
// internally). Router guards run outside of any component, so we borrow the app's context via
// runWithContext instead of calling useFlag() from within a page/component.
function isRouteFeatureEnabled(app, flagName) {
  return app.runWithContext(() => {
    const flag = useFlag(flagName);

    return new Promise(resolve => {
      if (flag.value.ready || flag.value.error) {
        resolve(Boolean(flag.value.enabled));
        return;
      }

      const stopWatch = watch(flag, state => {
        if (state.ready || state.error) {
          stopWatch();
          resolve(Boolean(state.enabled));
        }
      });

      setTimeout(() => {
        stopWatch();
        resolve(false);
      }, FEATURE_FLAG_TIMEOUT_MS);
    });
  });
}

function addCatchToRouterFunc(router, funcName) {
  const originalFunc = router[funcName];
  router[funcName] = async function wrappedRouterFunc(...args) {
    try {
      const result = await originalFunc.apply(this, args);
      return result;
    } catch (e) {
      onError(e);
    }
  };
}

// Routes are located in the individual module folders
// Import all of the module routes files.
async function loadLocalRoutes() {
  const context = import.meta.glob('@/modules/**/routes.js');
  const routes = await Promise.all(
    Object.keys(context).map(async key => {
      const module = await context[key]();
      return module.default;
    })
  );
  return routes;
}

// We needed a way to log in a user from the warehouse portal, and to land a carrier here after
// they sign in on Helios. Carriers arrive with a ?handoff flag (or a legacy ?token); the Auth
// store exchanges the HttpOnly cookie for the real token server-side.
// @returns {Promise<boolean>} true if navigation was resolved (next() was called)
async function handleHandoffAuth(store, to, next) {
  if (!(await store.dispatch('Auth/authenticateFromHandoff', to.query))) {
    return false;
  }

  // Only proceed once the token has actually authenticated a user; otherwise send them to login.
  if (!store.getters['Auth/me']?.id) {
    routeHelpers.setAppLoadedTrue(store);
    next({ name: 'login', query: buildLoginQueryWithMcpTransaction(to.query) });
    return true;
  }

  const me = store.getters['Auth/me'];
  const mcpTransaction = readMcpTransaction(to.query);

  if (mcpTransaction && isCarrierUser(me)) {
    if (await completeMcpPortalLogin({ mcpTransaction, mcpUrl: config.mcp_url })) {
      return true;
    }
  }

  if (mcpTransaction && !isCarrierUser(me)) {
    // Full-page hop: do not next() — token is cleared and Helios replace is in flight
    // (same as tryCompleteOrHopMcpLogin).
    await store.dispatch('Auth/logInToWarehousePortal', { mcpTransaction });
    return true;
  }

  let parsedNextRoute = null;
  try {
    const parsed = to.query?.nextRoute ? JSON.parse(to.query.nextRoute) : null;
    parsedNextRoute =
      parsed && typeof parsed === 'object' && typeof parsed.name === 'string' ? parsed : null;
  } catch {
    parsedNextRoute = null;
  }
  // 'warehouse.select' is the /book screen (the carrier home). The previous { name: 'book' }
  // matched no route, stranding the user on the root URL with an empty page.
  // Preserve mcp_txn on fallback so a failed MCP mint can retry (Helios handoff parity).
  const nextRoute = withMcpTransactionQuery(
    parsedNextRoute ?? { name: 'warehouse.select' },
    mcpTransaction
  );

  const warehouseId = parsedNextRoute?.params?.warehouseId;
  if (warehouseId) {
    await store.commit('Auth/setPreviewWarehouseId', warehouseId);
  }

  routeHelpers.setAppLoadedTrue(store);
  next(nextRoute);
  return true;
}

async function handleAuthenticatedUserNavigation({ store, app, to, next, authenticatedUser }) {
  if (
    routeHelpers.redirectYardDriverToCirce({
      user: authenticatedUser,
      circeUrl: config.circe_url
    })
  ) {
    return;
  }

  // Helios beforeEach parity: retry MCP complete (or warehouse hop) whenever mcp_txn is on `to`.
  const mcpTransaction = readMcpTransaction(to.query);
  if (await tryCompleteOrHopMcpLogin({ store, me: authenticatedUser, mcpTransaction })) {
    return;
  }

  if (!isCarrierUser(authenticatedUser)) {
    // Full-page hop to Helios — skip next() after clearing the Luna session.
    await store.dispatch('Auth/logInToWarehousePortal');
    return;
  }
  // Force redirect to company registration for auth pages if they don't have one already
  if (!authenticatedUser.companyId && to.name !== 'register.company') {
    next(withMcpTransactionQuery({ name: 'register.company' }, mcpTransaction));
    return;
  }
  if (to.meta.featureFlag && !(await isRouteFeatureEnabled(app, to.meta.featureFlag))) {
    next(withMcpTransactionQuery({ name: 'warehouse.select' }, mcpTransaction));
    return;
  }
  next();
}

async function initializeRouter(store, app) {
  let routes = [
    // Moduleless routes can go here
    {
      path: '/',
      name: 'home',
      redirect: { name: 'warehouse.select' },
      meta: {
        requiresAuth: false
      }
    }
  ];

  const moduleRoutes = await loadLocalRoutes();
  routes = routeHelpers.registerRoutes(routes, moduleRoutes);

  const router = createRouter({
    history: createWebHistory(),
    routes
  });

  addCatchToRouterFunc(router, 'push');
  addCatchToRouterFunc(router, 'replace');

  router.beforeEach(async (to, from, next) => {
    if (!store.getters['App/s3BaseUrl']) {
      await store.dispatch('App/getAppEnvironment', {}, { root: true });
    }

    if (await handleHandoffAuth(store, to, next)) {
      return;
    }

    // Check if the from route is dirty (has unsaved data), if it is then ask user if they want to proceed
    if (await routeHelpers.checkDirty(store, from)) {
      return;
    }

    routeHelpers.setPageTitle(store, to, config.application_name);

    const authRequired = routeHelpers.isAuthRequired(to);

    if (!authRequired) {
      routeHelpers.setAppLoadedTrue(store);
      next();
      return;
    }

    const authenticatedUser = await store.dispatch('Auth/getAuthenticatedUser');
    if (!authenticatedUser) {
      routeHelpers.setAppLoadedTrue(store);
      next({
        name: 'login',
        query: buildLoginQueryWithMcpTransaction(to.query, {
          redirectPath: to.path
        })
      });
      return;
    }
    await handleAuthenticatedUserNavigation({ store, app, to, next, authenticatedUser });
  });

  return router;
}

export default initializeRouter;
